import { NavLink, useNavigate, Outlet, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, User, Briefcase, GraduationCap, Sparkles, Wrench, Heart,
  Compass, Users, FilePlus2, Files, FolderArchive, ShieldCheck, LogOut, Menu, Leaf, Shield, Upload, MessagesSquare, LifeBuoy, ClipboardCheck, Route, Award, Mail,
} from "lucide-react";

const NAV = [
  { to: "/app", label: "Översikt", icon: LayoutDashboard, end: true },
  { to: "/sa-funkar-det", label: "Så funkar det", icon: Compass, guide: true },
  { to: "/app/profil", label: "Min profil", icon: User },
  { to: "/app/importera-cv", label: "Ladda upp CV", icon: Upload },
  { to: "/app/erfarenheter", label: "Erfarenheter", icon: Briefcase },
  { to: "/app/utbildning", label: "Utbildning", icon: GraduationCap },
  { to: "/app/intyg", label: "Betyg & intyg", icon: Award },
  { to: "/app/kompetenser", label: "Kompetenser", icon: Sparkles },
  { to: "/app/system", label: "System och verktyg", icon: Wrench },
  { to: "/app/karaktar", label: "Karaktär och styrkor", icon: Heart },
  { to: "/app/intervju", label: "Guidad intervju", icon: MessagesSquare },
  { to: "/app/intressen", label: "Intressen och drivkrafter", icon: Compass },
  { to: "/app/riktning", label: "Framtidsriktning", icon: Compass },
  { to: "/app/vagledning", label: "Sök jobb", icon: Route },
  { to: "/app/referenser", label: "Referenser", icon: Users },
  { to: "/app/underlag", label: "Mitt underlag", icon: ClipboardCheck },
  { to: "/app/skapa-cv", label: "Skapa CV", icon: FilePlus2, highlight: true },
  { to: "/app/personligt-brev", label: "Personligt brev", icon: Mail },
  { to: "/app/meddelanden", label: "Meddelanden", icon: MessagesSquare },
  { to: "/app/cv", label: "Mina CV:n", icon: Files },
  { to: "/app/ansokningar", label: "Mina ansökningar", icon: Briefcase },
  { to: "/app/dokument", label: "Mina dokument", icon: FolderArchive },
  { to: "/app/installningar", label: "Inställningar och integritet", icon: ShieldCheck },
  { to: "/app/feedback", label: "Hjälp & feedback", icon: LifeBuoy },
];

function NavItems({ onNavigate, isAdmin, adminBadge = 0, msgBadge = 0 }) {
  const items = isAdmin ? [...NAV, { to: "/app/admin", label: "Adminpanel", icon: Shield, badge: adminBadge }] : NAV;
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((item) => {
        const Icon = item.icon;
        const itemBadge = item.to === "/app/meddelanden" ? msgBadge : (item.badge || 0);
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.to.replace(/\//g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary font-semibold"
                  : item.guide
                  ? "text-primary font-semibold bg-primary/[0.06] border border-primary/25 hover:bg-primary/10"
                  : item.highlight
                  ? "text-accent font-semibold hover:bg-accent/10"
                  : "text-foreground/70 hover:bg-muted hover:text-foreground"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px] shrink-0" />
            <span className="truncate">{item.label}</span>
            {item.guide && (
              <span className="ml-auto rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white" data-testid="nav-guide-badge">
                Guide
              </span>
            )}
            {itemBadge > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-semibold text-white" data-testid={`nav-badge-${item.to.replace(/\//g, "-")}`}>
                {itemBadge}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3 px-5 py-5">
      <img src="/hamn-logo.png" alt="HAMN CV Studio" className="h-12 w-12 rounded-xl object-cover" />
      <div className="leading-tight">
        <div className="font-head font-bold text-lg text-primary">HAMN</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">CV Studio</div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/feedback/unread-count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (!user || user.role === "employer") return;
    const load = () => api.get("/notifications").then((r) => setMsgUnread(r.data.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  const onLogout = () => { logout(); navigate("/login"); };

  if (user?.role === "employer") return <Navigate to="/foretag" replace />;

  return (
    <div className="hamn-bg min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 flex-col border-r border-border bg-white/80 backdrop-blur-xl">
        <Brand />
        <div className="flex-1 overflow-y-auto pb-4"><NavItems isAdmin={user?.role === "admin"} adminBadge={unread} msgBadge={msgUnread} /></div>
        <div className="border-t border-border p-4">
          <div className="mb-3 rounded-xl bg-muted px-3 py-2">
            <div className="text-xs text-muted-foreground">Inloggad som</div>
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] font-mono text-primary">{user?.candidate_id}</div>
          </div>
          <Button data-testid="logout-btn" variant="outline" className="w-full rounded-full" onClick={onLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Logga ut
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border bg-white/90 backdrop-blur-xl px-4 py-3">
        <Sheet>
          <SheetTrigger asChild>
            <Button data-testid="mobile-menu-btn" variant="ghost" size="icon"><Menu /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <Brand />
            <div className="overflow-y-auto h-[calc(100vh-160px)]"><NavItems isAdmin={user?.role === "admin"} adminBadge={unread} msgBadge={msgUnread} /></div>
            <div className="p-4 border-t border-border">
              <Button data-testid="logout-btn-mobile" variant="outline" className="w-full rounded-full" onClick={onLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Logga ut
              </Button>
            </div>
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2.5">
          <img src="/hamn-logo.png" alt="HAMN" className="h-8 w-8 rounded-lg object-cover" />
          <div className="font-head font-bold text-primary">HAMN CV Studio</div>
        </div>
        <div className="text-[11px] font-mono text-primary">{user?.candidate_id}</div>
      </header>

      <main className="lg:pl-72">
        <div className="mx-auto max-w-6xl px-4 py-6 md:px-8 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
